import { ArrowDownIcon, ArrowUpIcon } from "@chakra-ui/icons";
import {
  Flex,
  Heading,
  HStack,
  Input,
  InputGroup,
  InputRightAddon,
  Kbd,
  Text,
  Tooltip,
  useToast,
} from "@chakra-ui/react";
import GamelightButton from "components/button";
import React, { useCallback, useEffect, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import Chart from "react-apexcharts";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCampaignCreate } from "views/admin/campaign_create/roas/components/CampaignCreateContext";
import { useStep } from "views/admin/campaign_create/roas/components/StepContext";
import { Put } from "api";
import { useSelector } from "react-redux";
import { RootReducer } from "RootReducer";
import { useQuery } from "hooks/useQuery";
import {
  hideGlobalLoading,
  showGlobalLoading,
} from "helper/GlobalLoadingDispatches";

const roasSchema = z.object({
  D0: z.string().optional(),
  D7: z.string().min(1, "D7 is required"),
  D30: z.string().min(1, "D30 is required"),
  D90: z.string().optional(),
  D180: z.string().optional(),
  D365: z.string().optional(),
});

type RoasFormData = z.infer<typeof roasSchema>;

const ROAS_TARGETS: {
  label: string;
  key: keyof RoasFormData;
  mandatory?: boolean;
}[] = [
  { label: "D0", key: "D0" },
  { label: "D7", key: "D7", mandatory: true },
  { label: "D30", key: "D30", mandatory: true },
  { label: "D90", key: "D90" },
  { label: "D180", key: "D180" },
  { label: "D365", key: "D365" },
];

const RoasTargets = () => {
  const toast = useToast();
  const { handleUpdateRoasGoals, roasGoals } = useCampaignCreate();
  const { nextStep, markStepUncompleted } = useStep();
  const {
    setFocus,
    control,
    watch,
    handleSubmit,
    setError,
    setValue,
    formState: { isValid, ...restFormState },
  } = useForm<RoasFormData>({
    resolver: zodResolver(roasSchema),
    defaultValues: {
      D0: "",
      D7: "",
      D30: "",
      D90: "",
      D180: "",
      D365: "",
    },
  });

  const [focusedIndex, setFocusedIndex] = React.useState<number>(-1);
  const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);

  const searchParams = useQuery();
  const campaignId = searchParams.get("id");

  const report = useSelector((state: RootReducer) => state.overview.report);
  const campaigns = report ? report.campaigns : [];

  useEffect(() => {
    if (roasGoals) {
      roasGoals.D0 && setValue("D0", roasGoals.D0);
      roasGoals.D7 && setValue("D7", roasGoals.D7);
      roasGoals.D30 && setValue("D30", roasGoals.D30);
      roasGoals.D90 && setValue("D90", roasGoals.D90);
      roasGoals.D180 && setValue("D180", roasGoals.D180);
      roasGoals.D365 && setValue("D365", roasGoals.D365);
    }
  }, []);

  const handleKeyDown = useCallback(
    (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
      const key = e.key;

      switch (key) {
        case "ArrowDown":
        case "ArrowUp":
        case "Enter":
          e.preventDefault();
          const newIndex = key === "ArrowUp" ? index - 1 : index + 1;
          if (newIndex >= 0 && newIndex < ROAS_TARGETS.length) {
            setFocus(ROAS_TARGETS[newIndex].key, {
              shouldSelect: true,
            });
          }
          if (newIndex === ROAS_TARGETS.length && key === "Enter") {
            handleSubmit(onSubmit)();
          }
          break;

        default:
          break;
      }
    },
    [setFocus]
  );

  const values = watch();

  const chartData = useMemo(() => {
    return ROAS_TARGETS.map((target) => ({
      x: target.label,
      y: parseFloat(values[target.key] || "0"),
    }));
  }, [values]);

  const chartOptions: ApexCharts.ApexOptions = {
    chart: {
      type: "bar",
      height: 350,
      toolbar: {
        show: false,
      },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: "55%",
      },
    },
    dataLabels: {
      enabled: false,
    },
    xaxis: {
      categories: ROAS_TARGETS.map((target) => target.label),
      title: {
        text: "Days",
      },
    },
    yaxis: {
      title: {
        text: "ROAS %",
      },
    },
    tooltip: {
      y: {
        formatter: (value: any) => `${value}%`,
      },
    },
    stroke: {
      show: false,
    },
    colors: ["#422AFB"],
  };

  const series = [
    {
      name: "ROAS",
      data: chartData,
    },
  ];

  const onSubmit = useCallback(
    async (data: RoasFormData) => {
      const _data: { [key: string]: number } = {};

      for (const [key, value] of Object.entries(data)) {
        const newKey = key.toUpperCase();
        const newValue = parseFloat(value) / 100;
        _data[newKey] = newValue;
      }

      let prevValue = -Infinity;
      let isError = false;
      for (const key of Object.keys(_data)) {
        if (_data[key] !== undefined) {
          if (_data[key] <= 0) {
            setError(key as keyof RoasFormData, {
              message: "The value must be greater than 0 or leave it empty.",
            });

            toast({
              title: "Error",
              description: `${key} value must be greater than 0 or leave it empty.`,
              status: "error",
              duration: 5000,
              isClosable: true,
            });

            isError = true;
          }

          if (_data[key] <= prevValue) {
            setError(key as keyof RoasFormData, {
              message: "The value must be greater than the previous value.",
            });

            toast({
              title: "Error",
              description: `${key} value must be greater than the previous value.`,
              status: "error",
              duration: 5000,
              isClosable: true,
            });

            isError = true;
          }
          prevValue = _data[key];
        }
      }

      if (isError) {
        markStepUncompleted(1);
        return;
      }

      try {
        setIsSubmitting(true);

        if (campaignId) {
          showGlobalLoading("Updating ROAS goals");
          const response = await Put(
            `/dashboard/campaigns/${campaignId}/goals`,
            _data
          );
          if (response.err) {
            toast({
              title: "Error",
              description: response.err,
              status: "error",
              duration: 5000,
              isClosable: true,
            });
            return;
          }

          toast({
            title: "Success",
            description: "ROAS goals updated successfully",
            status: "success",
            duration: 5000,
            isClosable: true,
          });
        }

        handleUpdateRoasGoals(data);
        nextStep(true);
      } catch (error) {
        console.error("Error while submitting ROAS Targets", error);
      } finally {
        setIsSubmitting(false);
        hideGlobalLoading();
      }
    },
    [handleUpdateRoasGoals, nextStep, campaignId]
  );

  return (
    <Flex
      flex={1}
      flexDir="column"
      gap={8}
      pl={24}
      pr={32}
      h={`calc(88vh - 140px)`}
    >
      <Flex alignItems={"center"} flex={1} gap={8}>
        <Flex
          flexDir={"column"}
          gap={2}
          flex={1}
          h="full"
          justifyContent={"center"}
        >
          {ROAS_TARGETS.map((target, index) => (
            <Flex
              key={`roas-${target.label}`}
              gap={2}
              alignItems="flex-end"
              justifyContent={"flex-end"}
              transition={"all 0.5s ease"}
              transform={`scale(${index === focusedIndex ? 1.2 : 1})`}
              onClick={() => setFocus(target.key)}
            >
              <HStack>
                <Tooltip
                  placement="top"
                  hasArrow
                  label={`This target is mandatory for the campaign.`}
                >
                  <Heading>
                    {target.mandatory && <Text color={"red.500"}>*</Text>}
                  </Heading>
                </Tooltip>
                <Heading
                  as={"span"}
                  color={index === focusedIndex ? "gray.800" : "gray.500"}
                  textTransform={"uppercase"}
                  fontWeight={index === focusedIndex ? "bold" : "semibold"}
                  transition="all 0.5s ease"
                >
                  {target.label}:
                </Heading>
              </HStack>
              <Controller
                name={target.key}
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <InputGroup w={36} h={12}>
                    <Input
                      {...field}
                      onFocus={() => setFocusedIndex(index)}
                      onBlur={() => {
                        field.onBlur && field.onBlur();
                        setFocusedIndex(-1);
                      }}
                      type="number"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      w={32}
                      h={12}
                      transition="all 0.5s ease"
                      _focus={{
                        borderBottomColor: "gray.800",
                      }}
                      _hover={{}}
                      bgColor="transparent"
                      borderWidth={0}
                      borderBottomWidth={4}
                      borderBottomColor={error ? "red.500" : "gray.500"}
                      borderRadius={0}
                      mb={2}
                      textAlign="center"
                      fontWeight="black"
                      color="brand.500"
                      fontSize="2xl"
                      onKeyDown={(e) => handleKeyDown(index, e)}
                    />
                    <InputRightAddon
                      bgColor={"transparent"}
                      border={0}
                      children="%"
                      h={12}
                      pl={2}
                      borderWidth={0}
                      borderBottomWidth={4}
                      borderBottomColor={error ? "red.500" : "gray.500"}
                      borderRadius={0}
                      alignItems={"flex-end"}
                      justifyContent={"flex-end"}
                    />
                  </InputGroup>
                )}
              />
            </Flex>
          ))}
        </Flex>
        <Flex
          h="full"
          flex={4}
          flexDir="column"
          alignItems={"center"}
          justifyContent="center"
          gap={8}
        >
          <Flex flex={1} flexDir="column" justifyContent={"flex-end"}>
            <Text color={"gray.600"} fontWeight="medium">
              You can use <Kbd>Tab</Kbd> or <Kbd>Enter</Kbd> or{" "}
              <Kbd>
                <ArrowUpIcon />
              </Kbd>{" "}
              and{" "}
              <Kbd>
                <ArrowDownIcon />
              </Kbd>{" "}
              to navigate between fields.
            </Text>
          </Flex>
          <Flex flex={2} w={"100%"} pl={16}>
            <Chart
              options={chartOptions}
              series={series}
              type="bar"
              height={350}
              width={500}
            />
          </Flex>
        </Flex>
      </Flex>
      <GamelightButton
        disabled={!isValid || isSubmitting}
        isLoading={isSubmitting}
        onClick={handleSubmit(onSubmit)}
        loadingText="Processing"
      >
        Save ROAS Goals
      </GamelightButton>
    </Flex>
  );
};

export default RoasTargets;
